import { message } from "antd";
import { makeAutoObservable } from "mobx";
import {
  connectStore,
  createCustomDomain,
  deleteApplication,
  getCustomDomain,
  listingApplication,
  registerApplication,
  updateFacebookToken,
} from "../../apis/applicationApi";
import { createConfig, getConfig, handleFileCertificate } from "../../apis/configOutput";
import { LOCAL_STORAGE_TYPE, getLocalStorage, setLocalStorage } from "../../common/localStorage";
import { globalStores } from "../../GlobalStores";
import { demoAppData } from "../../mock/demoApp";
import { DEMO_APP_ID } from "../../common/constants";

// Lưu Customize Domain: domain đã được app của đối tác khác dùng (auth-service)
const DOMAIN_USED_BY_OTHER_PARTNER = 523;

// Cột allowlist (JSON mảng chuỗi hoặc null) → mảng, chịu được cả chuỗi JSON
const toList = (value) => {
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return [];
    }
  }
  return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
};

class ApplicationStores {
  arrApplication = [];
  currentApp = undefined;
  currentConfig = null;
  loading = false;
  currentCustomDomain = null;
  keyFirebase = null;

  constructor() {
    makeAutoObservable(this);
  }

  async getAllApplication() {
    this.setLoading(true);
    const res = await listingApplication(3000, 0);
    this.setLoading(false);
    const data = res.dataArray;
    this.setArrApplication(data);
    if (data?.length) {
      const curAppId = getLocalStorage(LOCAL_STORAGE_TYPE.CURRENT_APP_ID);
      if (curAppId === DEMO_APP_ID) {
        this.setCurrentApp(demoAppData);
      } else {
        const curApp = data.find((item) => item.app_id === curAppId);
        this.setCurrentApp(curApp || data[0]);
      }
    }
  }

  async createApplication(param) {
    const res = await registerApplication(param);
    if (res.responseCode === 201) {
      message.success(globalStores.t("Successfully"));
      this.getAllApplication();
    } else {
      message.error(globalStores.t("Failed"));
    }
  }

  // Lưu thông tin app (không có type) hoặc kết nối iOS / Android / Web. Response: true nếu thành công
  async connectAppStore(param) {
    try {
      const res = await connectStore(param);
      // 201: tạo / cập nhật kết nối, 203: cập nhật (thông tin app, iOS đã phát hành)
      if ([201, 203].includes(res?.responseCode)) {
        message.success(globalStores.t("Successfully"));
        this.getAllApplication();
        return true;
      }
      message.error(globalStores.t("Failed"));
      return false;
    } catch (error) {
      message.error(globalStores.t("Failed"));
      return false;
    }
  }

  async putTokenFacebook(userProfile, appId) {
    try {
      await updateFacebookToken({ app_id: appId, ...userProfile });
      message.success(globalStores.t("Successfully"));
    } catch (error) {
      message.error(globalStores.t("Failed"));
    }
  }

  async deleteApplication(appId) {
    try {
      await deleteApplication(appId);
      message.success(globalStores.t("Successfully"));
      this.getAllApplication();
    } catch (error) {
      message.error(globalStores.t("Failed"));
    }
  }

  async getConfigOutputByApp(appId) {
    const res = await getConfig(appId);

    const data = res?.data[0];

    if (data) {
      this.setCurrentConfig(JSON.parse(data?.value));
    } else {
      this.setCurrentConfig(null);
    }
  }

  // true: lưu thành công; false: lỗi (hiện thông báo của BE nếu có, vd: Send From cần token Netnam)
  async createConfigByApp(data, file) {
    try {
      await createConfig(data, file);
      message.success(globalStores.t("Successfully"));
      this.getAllApplication();
      return true;
    } catch (error) {
      const text = error?.data?.responseText;
      message.error(text ? globalStores.t(text) : globalStores.t("Failed"));
      return false;
    }
  }

  async handleFileCertificate(data) {
    try {
      const res = await handleFileCertificate(data);
      this.setKeyFirebase(res?.data);
    } catch (error) {
      message.error(globalStores.t("Failed"));
    }
  }

  // true khi tải được cấu hình (kể cả app chưa cấu hình gì), false khi lỗi
  async getCustomDomain(appId) {
    const res = await getCustomDomain(appId);
    if (!Array.isArray(res?.data)) {
      this.setCurrentCustomDomain(null);
      return false;
    }

    const data = res.data[0];

    if (data) {
      this.setCurrentCustomDomain({
        longlink: data?.dynamic_link_custom_domain,
        shortlink: data?.short_link_custom_domain,
        shorten: data?.shorten_custom_domain,
        nameInDeeplink: data?.name_in_deeplink,
        iconInDeeplink: data?.icon_in_deeplink,
        previewTitle: data?.preview_title,
        previewDescription: data?.preview_description,
        previewImageUrl: data?.preview_image_url,
        shortenPreviewTitle: data?.shorten_preview_title,
        shortenPreviewDescription: data?.shorten_preview_description,
        shortenPreviewImageUrl: data?.shorten_preview_image_url,
        allowReuseShortlink: !!data?.allow_reuse_shortlink,
        longLinkUseShortDomain: !!data?.long_link_use_short_domain,
        appName: data?.app_name || "",
        landingPage: data?.landing_page || null,
        shortenRedirectHostAllowlist: toList(data?.shorten_redirect_host_allowlist),
        deeplinkRedirectHostAllowlist: toList(data?.deeplink_redirect_host_allowlist),
        deeplinkSchemeAllowlist: toList(data?.deeplink_scheme_allowlist),
      });
    } else {
      this.setCurrentCustomDomain(null);
    }
    return true;
  }

  // Response: { saved, conflictFields } — conflictFields: tên trường domain (API) đã được đối tác khác dùng
  async createCustomDomain(param) {
    const res = await createCustomDomain(param);
    if (res?.responseCode === 203) {
      message.success(globalStores.t("Successfully"));
      this.getAllApplication();
      return { saved: true, conflictFields: [] };
    }
    if (res?.responseCode === DOMAIN_USED_BY_OTHER_PARTNER) {
      message.error(globalStores.t("This domain is already used by another partner"));
      return { saved: false, conflictFields: res.data?.fields || [] };
    }
    message.error(globalStores.t("Failed"));
    return { saved: false, conflictFields: [] };
  }

  setCurrentConfig(data) {
    this.currentConfig = data;
  }

  setCurrentCustomDomain(data) {
    this.currentCustomDomain = data;
  }

  setArrApplication(data) {
    this.arrApplication = data;
  }

  setCurrentApp(data) {
    this.currentApp = data;
    setLocalStorage(LOCAL_STORAGE_TYPE.CURRENT_APP_ID, data.app_id);
  }

  setLoading(data) {
    this.loading = data;
  }

  setKeyFirebase(data) {
    this.keyFirebase = data;
  }
}

export const applicationStores = new ApplicationStores();
